import { computed } from 'vue';
import { getCityBlogLink } from '@/config/cityBlogLinks';
import { providerInfo } from '@/components/data/providerData.js';
import { cityInfo, STUDENT_HUB_CITIES, STUDENT_HUB_SEO_CONTENT } from '@/components/data/cityData.js';
import { cityPriceStats } from '@/components/data/cityPriceStats.generated.js';
import { QUICK_LONDON_AREAS, LONDON_AREAS_SET } from '../../../shared/londonNeighborhoods.cjs';
import {
  listingsCount as NYC_SEO_LISTINGS,
  listingsLabel as NYC_SEO_LISTINGS_LABEL,
} from '../../../shared/nycMarketStats.cjs';
import { listingsLabel as LONDON_SEO_LISTINGS_LABEL, listingsCount as LONDON_SEO_LISTINGS, operatorsCount as LONDON_SEO_OPERATORS } from '../../../shared/londonMarketStats.cjs';
import { getGscSearchMeta } from '../../../shared/gscSearchIntent.cjs';
import { SITE_METRICS } from '@/config/siteMetrics';

export const NYC_CITIES = new Set([
  'Manhattan', 'Brooklyn', 'Queens', 'Jersey City', 'New York', 'Williamsburg', 'Astoria',
]);
export const LONDON_AREAS = LONDON_AREAS_SET;

export const quickCities = [
  { slug: 'madrid', label: 'Madrid' },
  { slug: 'barcelona', label: 'Barcelona' },
  { slug: 'valencia', label: 'Valencia' },
  { slug: 'malaga', label: 'Málaga' },
  { slug: 'sevilla', label: 'Sevilla' },
  { slug: 'salamanca', label: 'Salamanca' },
];

export const quickLondonAreas = QUICK_LONDON_AREAS;

export const quickNYCAreas = [
  { slug: 'brooklyn', label: 'Brooklyn' },
  { slug: 'manhattan', label: 'Manhattan' },
  { slug: 'queens-nyc', label: 'Queens' },
  { slug: 'jersey-city', label: 'Jersey City' },
  { slug: 'williamsburg', label: 'Williamsburg' },
  { slug: 'astoria', label: 'Astoria' },
];

/**
 * SEO titles, breadcrumbs, city/provider content and quick-nav chips.
 */
export function useSearchSeoContent(deps) {
  const {
    route,
    t,
    regionId,
    filters,
    currentCity,
    isStudentMode,
    isProviderSearch,
    currentProvider,
    currentPriceSlug,
  } = deps;

  const currentCityBlogLink = computed(() => {
    if (regionId.value !== 'es') return null;
    if (!route.params.city || isProviderSearch.value) return null;
    return getCityBlogLink(String(route.params.city));
  });

  const seoH1Title = computed(() => {
    const city = currentCity.value;
    const maxPrice = filters.value.maxPrice;
    const provider = currentProvider.value;
    const providerData = provider ? providerInfo[provider] : null;
    const isUS = regionId.value === 'us' || (city != null && NYC_CITIES.has(city));
    const isUK = regionId.value === 'uk' || (city != null && LONDON_AREAS.has(city));

    const lang = route.path.toLowerCase().includes('/findyourcoliving') ? 'en' : 'es';
    const isHub = route.path === '/encuentratucoliving' || route.path === '/findyourcoliving';
    if (!isStudentMode.value && !isProviderSearch.value) {
      const gscH1 = getGscSearchMeta({
        pageType: isHub ? 'hub' : (city ? 'city' : null),
        lang,
        region: isUK ? 'uk' : isUS ? 'us' : 'es',
        city,
        maxPrice: maxPrice || null,
        isStudentMode: isStudentMode.value,
        metrics: {
          roomsEs: SITE_METRICS.roomCountLabel,
          minPriceEs: SITE_METRICS.minPrice,
          londonListings: LONDON_SEO_LISTINGS,
          londonListingsLabel: LONDON_SEO_LISTINGS_LABEL,
          londonOperators: LONDON_SEO_OPERATORS,
          nycListings: NYC_SEO_LISTINGS,
          nycListingsLabel: NYC_SEO_LISTINGS_LABEL,
          citySeoCount: city && cityPriceStats[city]?.listingsLabel,
          cityMinPrice: city && cityPriceStats[city]?.priceRange?.split('-')[0]?.trim(),
        },
      })?.h1;
      if (gscH1) return gscH1;
    }

    if (isUS) {
      if (provider && providerData) {
        if (city) return `${providerData.fullName} in ${city}: Rooms & Prices`;
        return `${providerData.fullName}: Coliving NYC — Furnished Rooms`;
      }
      if (city && maxPrice) return `Coliving in ${city} up to $${maxPrice}/mo`;
      if (city) {
        const h1Override = cityInfo[city]?.h1Title;
        if (h1Override) return h1Override;
        return `Coliving in ${city} NYC — Furnished Rooms & Prices`;
      }
      return `Coliving in New York City — ${NYC_SEO_LISTINGS_LABEL} Furnished Rooms from $1,200/mo`;
    }

    if (isUK) {
      if (provider && providerData) {
        if (city) return `${providerData.fullName} in ${city}: Rooms & Prices`;
        return `${providerData.fullName}: Coliving London — Furnished Rooms`;
      }
      if (city && maxPrice) return `Coliving in ${city} up to £${maxPrice}/mo`;
      if (city === 'London') return `Coliving London 2026: ${LONDON_SEO_LISTINGS_LABEL} Furnished Rooms from £800/mo`;
      if (city) return `Coliving in ${city}, London — Furnished Rooms & Prices`;
      return `Coliving London 2026: ${LONDON_SEO_LISTINGS_LABEL} Furnished Rooms from £800/mo`;
    }

    if (isProviderSearch.value && providerData) {
      if (city) return `${providerData.fullName} en ${city}: Habitaciones y Precios`;
      return `${providerData.fullName}: Colivings y Residencias en España`;
    }

    if (isStudentMode.value) {
      if (city && maxPrice) {
        if (maxPrice <= 500) return `Residencias baratas en ${city}`;
        return `Residencias universitarias en ${city} hasta ${maxPrice}€/mes`;
      }
      if (city) {
        const studentH1 = cityInfo[city]?.studentH1Title;
        return studentH1 || `Residencia Estudiantes ${city} 2026`;
      }
      return 'Residencias universitarias en España 2026';
    }

    const accommodationType = route.meta?.accommodationType;
    if (accommodationType) {
      const baseKey = `accommodationLandings.${accommodationType}`;
      if (city) return t(`${baseKey}.h1City`, { city });
      return t(`${baseKey}.h1`);
    }

    if (city && maxPrice) {
      if (maxPrice <= 500) return `Colivings Baratos en ${city} (hasta ${maxPrice}€)`;
      if (maxPrice <= 700) return `Colivings Económicos en ${city} (hasta ${maxPrice}€)`;
      return `Colivings en ${city} hasta ${maxPrice}€/mes`;
    }

    if (city) {
      const h1Override = cityInfo[city]?.h1Title;
      if (h1Override) return h1Override;
      return `Colivings en ${city}: Encuentra tu Habitación Ideal`;
    }

    if (route.path === '/findyourcoliving') {
      return `Coliving NYC & London — ${NYC_SEO_LISTINGS_LABEL} Furnished Rooms, No Broker Fee`;
    }
    if (route.path === '/encuentratucoliving') {
      return 'Alquiler de habitaciones, coliving y apartamentos amueblados en España';
    }

    return t('colivingSearch.seo.defaultTitle');
  });

  const seoH2Title = computed(() => {
    const city = currentCity.value;
    if (!city) return '';
    if (isStudentMode.value) {
      const studentH1 = cityInfo[city]?.studentH1Title;
      if (studentH1) return studentH1;
    }
    const h1Override = cityInfo[city]?.h1Title;
    if (h1Override) return h1Override;
    const isUS = regionId.value === 'us' || NYC_CITIES.has(city);
    const isUK = regionId.value === 'uk' || LONDON_AREAS.has(city);
    if (isUS) {
      if (isStudentMode.value) return `Student accommodation in ${city}`;
      if (city === 'Brooklyn') return 'Furnished rooms & shared apartments — month-to-month, no broker fee';
      if (city === 'Queens') return 'Cheap coliving & furnished rooms — NYC\'s best value borough';
      if (city === 'Jersey City') return 'Furnished rooms with PATH to Manhattan — lower rent than NYC';
      return `Coliving & furnished rooms for rent in ${city}`;
    }
    if (isUK) {
      return isStudentMode.value ? `Student accommodation in ${city}` : `Coliving & furnished rooms in ${city}`;
    }
    if (isStudentMode.value) return `Residencias universitarias en ${city}`;
    return `Habitaciones, coliving y alquiler flexible en ${city}`;
  });

  const currentCityContent = computed(() => {
    const city = currentCity.value;
    if (!city || !cityInfo[city]) return null;
    const base = cityInfo[city];
    const live = cityPriceStats[city];
    if (!live) return base;
    return {
      ...base,
      avgPrice: live.avgPriceDisplay ?? live.avgPrice ?? base.avgPrice,
      priceRange: live.priceRange || base.priceRange,
      priceStatsUpdatedAt: live.generatedAt,
      liveListingsCount: live.totalListings,
      liveListingsLabel: live.listingsLabel,
    };
  });

  const currentProviderContent = computed(() => {
    const provider = currentProvider.value;
    if (!provider || !providerInfo[provider]) return null;
    return providerInfo[provider];
  });

  const visibleFaqs = computed(() => {
    if (isStudentMode.value && currentCityContent.value?.studentFaqs?.length) {
      return currentCityContent.value.studentFaqs;
    }
    if (!currentCityContent.value?.faqs) {
      if (isStudentMode.value) {
        return [
          { q: '¿Qué es una residencia de estudiantes?', a: 'Es alojamiento orientado a universitarios: habitación privada o compartida, zonas comunes y servicios incluidos. Puede ser residencia tradicional (curso completo) o coliving flex (desde 1 mes).' },
          { q: '¿Qué operadores aparecen en /residencias?', a: 'En /residencias listamos RESA, Nido, Xior, Livensa, Collegiate, Yugo y Urban Campus — residencias universitarias verificadas, no pisos sueltos.' },
          { q: '¿Cuánto cuesta una residencia de estudiantes en España?', a: 'Desde 320€/mes en Salamanca hasta 500€/mes en Madrid, todo incluido según ciudad. Madrid y Barcelona tienen la mayor variedad de operadores profesionales.' },
          { q: '¿Puedo reservar solo un semestre?', a: 'Sí, muchos colivings y residencias flex aceptan 3-6 meses. Ideal para Erasmus, prácticas o máster. Filtra por ciudad y compara el mínimo de estancia en cada ficha.' },
          { q: '¿Cómo reservo en Coolliving?', a: 'Busca por ciudad, envía tu solicitud con fechas, el operador aprueba tu perfil, firmas contrato digital y pagas. Sin comisiones de agencia.' },
        ];
      }
      return [
        { q: '¿Qué es un coliving?', a: 'Un coliving es un modelo de vivienda compartida con habitación privada y zonas comunes (cocina, salón, coworking). Incluye servicios como WiFi, limpieza y suministros en un precio fijo mensual.' },
        { q: '¿Cuánto cuesta un coliving en España?', a: 'Los precios varían por ciudad: Madrid desde 500€/mes, Barcelona desde 450€/mes, Valencia desde 350€/mes. Todos incluyen habitación y servicios.' },
        { q: '¿El coliving es mejor que alquilar piso?', a: 'El coliving ofrece contratos flexibles, sin fianzas abusivas, comunidad incluida y todos los gastos en un precio fijo. Ideal para estancias de 1-12 meses.' },
        { q: '¿Cómo reservo un coliving?', a: 'Proceso 100% online: busca, solicita, el operador aprueba, firmas contrato y pagas. ¡Sin comisiones de agencia!' },
      ];
    }
    return currentCityContent.value.faqs;
  });

  const breadcrumbItems = computed(() => {
    const provider = currentProvider.value;
    const providerData = provider ? providerInfo[provider] : null;

    const items = [
      { name: t('colivingSearch.breadcrumb.home'), to: '/' },
    ];

    if (isProviderSearch.value && providerData) {
      items.push({ name: t('colivingSearch.breadcrumb.colivings'), to: '/encuentratucoliving' });
      items.push({ name: providerData.fullName, to: `/encuentratucoliving/${provider}` });
      if (currentCity.value) {
        items.push({ name: currentCity.value, to: null });
      }
      return items;
    }

    items.push({
      name: isStudentMode.value
        ? t('colivingSearch.breadcrumb.studentHalls')
        : t('colivingSearch.breadcrumb.colivings'),
      to: isStudentMode.value ? '/residencias' : '/encuentratucoliving',
    });

    if (currentCity.value) {
      items.push({
        name: currentCity.value,
        to: `/${isStudentMode.value ? 'residencias' : 'encuentratucoliving'}/${currentCity.value.toLowerCase()}`,
      });
    }

    if (currentPriceSlug.value) {
      const priceLabel = t(`colivingSearch.breadcrumb.priceLabels.${currentPriceSlug.value}`)
        || `${filters.value.maxPrice}`;
      items.push({ name: priceLabel, to: null });
    }

    return items;
  });

  return {
    currentCityBlogLink,
    seoH1Title,
    seoH2Title,
    currentCityContent,
    currentProviderContent,
    visibleFaqs,
    breadcrumbItems,
    studentHubCities: STUDENT_HUB_CITIES,
    studentHubSeoContent: STUDENT_HUB_SEO_CONTENT,
  };
}
